/**
 * PnLCharts.jsx
 * Four bar-chart sections for the Yearly P&L view.
 * All data comes from the existing /api/pnl/yearly response — no new API calls.
 *
 * Charts (in order):
 *  1. Monthly Venue-wise Fixed Costs
 *  2. Monthly Venue-wise Revenue
 *  3. Monthly Venue-wise NOR
 *  4. All-Venues Monthly Comparison (consolidated)
 */
import React, { useMemo } from 'react';
import {
  ComposedChart, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { TrendingUp, TrendingDown, BarChart2 } from 'lucide-react';
import { Card } from './ui/card';

// ── Colour palette (up to 8 venues; loops if more) ───────────────────────────
const VENUE_PALETTE = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#14b8a6', // teal-500
];

const COST_COLOR    = '#3b82f6';   // blue
const REV_COLOR     = '#10b981';   // emerald
const NOR_POS_COLOR = '#16a34a';   // green-700
const NOR_NEG_COLOR = '#dc2626';   // red-600
const TREND_COLOR   = '#7c3aed';   // violet-700 — NOR trend line

const venueColor = (idx) => VENUE_PALETTE[idx % VENUE_PALETTE.length];

// ── Value formatters ─────────────────────────────────────────────────────────
const inr = (n) => {
  if (n === null || n === undefined) return '—';
  const v  = Number(n);
  const ab = Math.abs(v);
  const s  = v < 0 ? '-' : '';
  if (ab >= 10000000) return `${s}₹${(ab / 10000000).toFixed(2)}Cr`;
  if (ab >= 100000)   return `${s}₹${(ab / 100000).toFixed(2)}L`;
  if (ab >= 1000)     return `${s}₹${(ab / 1000).toFixed(1)}K`;
  return `${s}₹${ab.toFixed(0)}`;
};

const inrFull = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  // Separate trend entry from bar entries for cleaner rendering
  const bars  = payload.filter(p => p.dataKey !== 'NOR_trend');
  const trend = payload.find(p => p.dataKey === 'NOR_trend');
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xl p-3 text-xs min-w-[170px]">
      <p className="font-bold text-slate-700 mb-2 border-b pb-1">{label}</p>
      {bars.map((p, i) => {
        const isNeg = Number(p.value) < 0;
        return (
          <div key={i} className="flex items-center justify-between gap-4 py-0.5">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: p.fill || p.color }} />
              <span className="text-slate-600 truncate max-w-[110px]">{p.name}</span>
            </div>
            <span className="font-mono font-semibold ml-2" style={{ color: isNeg ? NOR_NEG_COLOR : (p.fill || p.color) }}>
              {inr(p.value)}
            </span>
          </div>
        );
      })}
      {trend && trend.value !== null && trend.value !== undefined && (
        <div className="flex items-center justify-between gap-4 pt-1.5 mt-1 border-t border-dashed border-slate-200">
          <div className="flex items-center gap-1.5">
            {/* Dashed line icon to match the trend line style */}
            <svg width="12" height="6" viewBox="0 0 12 6" className="flex-shrink-0">
              <line x1="0" y1="3" x2="4" y2="3" stroke={TREND_COLOR} strokeWidth="2" />
              <line x1="6" y1="3" x2="10" y2="3" stroke={TREND_COLOR} strokeWidth="2" />
            </svg>
            <span className="text-slate-500">NOR Trend</span>
          </div>
          <span className="font-mono font-semibold ml-2" style={{ color: TREND_COLOR }}>
            {inr(trend.value)}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Axis tick formatter ───────────────────────────────────────────────────────
const yAxisFmt = (v) => inr(v);

// ── Section header ────────────────────────────────────────────────────────────
const ChartHeader = ({ icon: Icon, title, subtitle, kpiLabel, kpiValue, isNOR }) => {
  const kpiNeg = isNOR && Number(kpiValue) < 0;
  return (
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-lg bg-slate-100">
          <Icon className="w-4 h-4 text-slate-600" />
        </div>
        <div>
          <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {kpiLabel && kpiValue !== undefined && (
        <div className="text-right">
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">{kpiLabel}</div>
          <div className={`font-bold font-mono text-sm ${kpiNeg ? 'text-red-600' : 'text-slate-700'}`}>
            {inrFull(kpiValue)}
          </div>
        </div>
      )}
    </div>
  );
};

// ── No-data overlay ───────────────────────────────────────────────────────────
const NoData = () => (
  <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
    No data available for this year
  </div>
);

// ── NOR bar fill helper ───────────────────────────────────────────────────────
// Returns green for positive, red for negative, grey for zero/missing
const norFill = (val) => {
  if (val === null || val === undefined) return '#cbd5e1';
  const n = Number(val);
  if (n > 0) return NOR_POS_COLOR;
  if (n < 0) return NOR_NEG_COLOR;
  return '#cbd5e1';
};

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────
export function PnLCharts({ yearlyData, venueAreas = {} }) {
  const { months = [], venues = [], year_totals = {} } = yearlyData || {};

  // ── Transform months array into flat chart-friendly rows ──────────────────
  const baseRows = useMemo(() =>
    months.map((m) => ({
      month:    m.label,
      isFuture: m.is_future,
      hasData:  m.has_data,
      fixed_costs:   m.fixed_costs   || {},
      total_revenue: m.total_revenue || {},
      net_margin:    m.net_margin    || {},
    })),
  [months]);

  // Chart 5 — Revenue per sq. ft. by venue (only venues with area data)
  const venuesWithArea = useMemo(
    () => venues.filter(v => (venueAreas[v] || {}).revenue_generating_area > 0),
    [venues, venueAreas]
  );
  const sqftData = useMemo(() =>
    baseRows.map((r) => {
      const row = { month: r.month, _future: r.isFuture };
      venuesWithArea.forEach(v => {
        const rga = (venueAreas[v] || {}).revenue_generating_area;
        const rev = r.isFuture ? null : (r.total_revenue[v] ?? 0);
        row[v] = rga && rev != null ? parseFloat((rev / rga).toFixed(4)) : null;
      });
      return row;
    }),
  [baseRows, venuesWithArea, venueAreas]);

  // Chart 1 — venue-wise fixed costs
  const costsData = useMemo(() =>
    baseRows.map((r) => {
      const row = { month: r.month, _future: r.isFuture, _hasData: r.hasData };
      venues.forEach(v => { row[v] = r.isFuture ? null : (r.fixed_costs[v] ?? 0); });
      return row;
    }),
  [baseRows, venues]);

  // Chart 2 — venue-wise revenue
  const revenueData = useMemo(() =>
    baseRows.map((r) => {
      const row = { month: r.month, _future: r.isFuture, _hasData: r.hasData };
      venues.forEach(v => { row[v] = r.isFuture ? null : (r.total_revenue[v] ?? 0); });
      return row;
    }),
  [baseRows, venues]);

  // Chart 3 — venue-wise NOR
  const norData = useMemo(() =>
    baseRows.map((r) => {
      const row = { month: r.month, _future: r.isFuture, _hasData: r.hasData };
      venues.forEach(v => { row[v] = r.isFuture ? null : (r.net_margin[v] ?? 0); });
      return row;
    }),
  [baseRows, venues]);

  // Chart 4 — consolidated (all venues, 3 series)
  // Missing (no-data, non-future) months are kept as 0 for BAR display,
  // but _hasData=false lets the trend regression skip them.
  const consolidatedData = useMemo(() =>
    baseRows.map((r) => ({
      month:         r.month,
      _future:       r.isFuture,
      _hasData:      r.hasData,
      'Fixed Costs': r.isFuture ? null : (r.fixed_costs._total  ?? 0),
      'Revenue':     r.isFuture ? null : (r.total_revenue._total ?? 0),
      // For bars: keep actual value (or 0 if no data saved yet); null = future
      'NOR':         r.isFuture ? null : (r.net_margin._total    ?? 0),
    })),
  [baseRows]);

  // Chart 4 — OLS linear regression trend line for NOR
  // KEY RULES:
  //   • Only use months where has_data=true (exclude future AND months with no PnL saved)
  //   • Do NOT treat missing months as zero — filter them out
  //   • Use the same month-index positions (0–11) so the trend sits on the same X-axis
  //   • NOR_trend is null for future/no-data months → connectNulls=false breaks the line cleanly
  const { trendData, trendSlope, trendInfo } = useMemo(() => {
    // Build regression points from months with ACTUAL recorded data only
    const pts = consolidatedData
      .map((d, i) => ({
        x:       i,
        y:       d['NOR'],
        future:  d._future,
        hasData: d._hasData,
      }))
      .filter(p => !p.future && p.hasData && p.y !== null && p.y !== undefined);

    if (pts.length < 2) return { trendData: consolidatedData, trendSlope: null, trendInfo: null };

    const n     = pts.length;
    const sumX  = pts.reduce((s, p) => s + p.x, 0);
    const sumY  = pts.reduce((s, p) => s + p.y, 0);
    const sumXY = pts.reduce((s, p) => s + p.x * p.y, 0);
    const sumX2 = pts.reduce((s, p) => s + p.x * p.x, 0);
    const denom = n * sumX2 - sumX * sumX;

    if (denom === 0) return { trendData: consolidatedData, trendSlope: 0, trendInfo: null };

    const slope     = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;

    // Evaluate the regression line at each month position.
    // null for future months AND months with no actual data — this breaks the line
    // cleanly at gaps and prevents the trend from spanning unsaved periods.
    const data = consolidatedData.map((d, i) => ({
      ...d,
      NOR_trend: (d._future || !d._hasData) ? null : Math.round(slope * i + intercept),
    }));

    // Endpoints for verification / display
    const firstPt = pts[0];
    const lastPt  = pts[pts.length - 1];
    const info = {
      n,
      slope,
      intercept,
      firstMonth: months[firstPt.x]?.label,
      lastMonth:  months[lastPt.x]?.label,
      firstTrend: Math.round(slope * firstPt.x + intercept),
      lastTrend:  Math.round(slope * lastPt.x  + intercept),
    };

    return { trendData: data, trendSlope: slope, trendInfo: info };
  }, [consolidatedData, months]);

  // Year totals for KPI badges
  const ytCosts   = year_totals.fixed_costs?._total   ?? 0;
  const ytRev     = year_totals.total_revenue?._total ?? 0;
  const ytNOR     = year_totals.net_margin?._total    ?? 0;

  if (!months.length || !venues.length) return null;

  // ── Shared chart props ────────────────────────────────────────────────────
  const commonGrid = <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />;
  const commonXAxis = (
    <XAxis
      dataKey="month"
      tick={{ fontSize: 11, fill: '#64748b' }}
      axisLine={false}
      tickLine={false}
    />
  );
  const commonYAxis = (
    <YAxis
      tickFormatter={yAxisFmt}
      tick={{ fontSize: 10, fill: '#94a3b8' }}
      axisLine={false}
      tickLine={false}
      width={64}
    />
  );
  const commonTooltip = <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc', radius: 4 }} />;
  const commonLegend = (
    <Legend
      wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
      iconType="square"
      iconSize={9}
    />
  );

  const chartHeight = 280;

  return (
    <div className="space-y-5 mt-6" data-testid="pnl-charts-section">
      {/* Section divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-slate-200" />
        <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest px-2">
          <BarChart2 className="w-3.5 h-3.5" /> Monthly Analytics
        </div>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* ── Chart 1: Fixed Costs by Venue ──────────────────────────────── */}
      <Card className="p-5" data-testid="pnl-chart-fixed-costs">
        <ChartHeader
          icon={TrendingDown}
          title="Monthly Venue-wise Fixed Costs"
          subtitle="Compare monthly fixed operating costs across all venues"
          kpiLabel="Year Total"
          kpiValue={ytCosts}
        />
        {costsData.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={costsData} barGap={2} barCategoryGap="25%">
              {commonGrid}
              {commonXAxis}
              {commonYAxis}
              {commonTooltip}
              {commonLegend}
              {venues.map((v, i) => (
                <Bar
                  key={v}
                  dataKey={v}
                  name={v}
                  fill={venueColor(i)}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={28}
                >
                  {costsData.map((entry, idx) => (
                    <Cell
                      key={`c1-${idx}`}
                      fill={entry._future ? '#e2e8f0' : venueColor(i)}
                      fillOpacity={entry._future ? 0.4 : entry._hasData ? 1 : 0.35}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
        <p className="text-[10px] text-slate-400 mt-2">
          Faded bars = no expense data saved for that month · Future months excluded
        </p>
      </Card>

      {/* ── Chart 2: Revenue by Venue ───────────────────────────────────── */}
      <Card className="p-5" data-testid="pnl-chart-revenue">
        <ChartHeader
          icon={TrendingUp}
          title="Monthly Venue-wise Revenue"
          subtitle="Total revenue per venue per month (day book + bulk + other)"
          kpiLabel="Year Total"
          kpiValue={ytRev}
        />
        {revenueData.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={revenueData} barGap={2} barCategoryGap="25%">
              {commonGrid}
              {commonXAxis}
              {commonYAxis}
              {commonTooltip}
              {commonLegend}
              {venues.map((v, i) => (
                <Bar
                  key={v}
                  dataKey={v}
                  name={v}
                  fill={venueColor(i)}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={28}
                >
                  {revenueData.map((entry, idx) => (
                    <Cell
                      key={`c2-${idx}`}
                      fill={entry._future ? '#e2e8f0' : venueColor(i)}
                      fillOpacity={entry._future ? 0.4 : 1}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* ── Chart 3: NOR by Venue ───────────────────────────────────────── */}
      <Card className="p-5" data-testid="pnl-chart-nor">
        <ChartHeader
          icon={ytNOR >= 0 ? TrendingUp : TrendingDown}
          title="Monthly Venue-wise NOR"
          subtitle="Net Operating Result per venue — venue colours match Charts 1 & 2"
          kpiLabel="Year NOR"
          kpiValue={ytNOR}
          isNOR
        />
        {/* Colour guide: venue colours + opacity convention */}
        <div className="flex flex-wrap items-center gap-4 mb-3">
          {venues.map((v, i) => (
            <span key={v} className="flex items-center gap-1.5 text-xs text-slate-700">
              <span className="w-3 h-3 rounded-sm inline-block" style={{ background: venueColor(i) }} />
              {v}
            </span>
          ))}
          <span className="text-xs text-slate-400 ml-auto">Full = profit · Faded (50%) = loss</span>
        </div>
        {norData.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={norData} barGap={2} barCategoryGap="25%">
              {commonGrid}
              {commonXAxis}
              <YAxis
                tickFormatter={yAxisFmt}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              {commonTooltip}
              {/* Legend driven by Bar.fill — shows venue colour correctly */}
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                iconType="square"
                iconSize={9}
              />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={1.5} />
              {venues.map((v, i) => (
                <Bar
                  key={v}
                  dataKey={v}
                  name={v}
                  fill={venueColor(i)}          /* venue palette — consistent with Charts 1 & 2 */
                  radius={[3, 3, 0, 0]}
                  maxBarSize={28}
                >
                  {norData.map((entry, idx) => {
                    const val = entry[v];
                    const isNeg  = Number(val) < 0;
                    const isZero = val === 0 || val === null;
                    return (
                      <Cell
                        key={`c3-${i}-${idx}`}
                        /* Keep venue colour; vary opacity to signal profit vs loss */
                        fill={entry._future ? '#e2e8f0' : venueColor(i)}
                        fillOpacity={
                          entry._future ? 0.3
                          : isZero      ? 0.2    /* no NOR data / genuinely zero */
                          : isNeg       ? 0.5    /* loss — same hue, faded */
                          :               1.0    /* profit — full colour */
                        }
                      />
                    );
                  })}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
        <p className="text-[10px] text-slate-400 mt-2">
          Reference line at ₹0 · Bars below the line are losses (50% opacity) · Bars above are profitable (full colour)
        </p>
      </Card>

      {/* ── Chart 4: Consolidated comparison ───────────────────────────── */}
      <Card className="p-5" data-testid="pnl-chart-consolidated">
        {/* Header with trend direction badge */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-slate-100">
              <BarChart2 className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-slate-800 text-sm">All-Venues Monthly Comparison</h3>
                {trendSlope !== null && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      trendSlope >= 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                    data-testid="pnl-trend-badge"
                  >
                    {trendSlope >= 0
                      ? <><TrendingUp className="w-3 h-3" /> NOR Improving</>
                      : <><TrendingDown className="w-3 h-3" /> NOR Declining</>
                    }
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Consolidated fixed costs, revenue &amp; NOR · dashed line = linear trend
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">Year NOR</div>
            <div className={`font-bold font-mono text-sm ${ytNOR < 0 ? 'text-red-600' : 'text-slate-700'}`}>
              {inrFull(ytNOR)}
            </div>
          </div>
        </div>

        {/* Colour guide */}
        <div className="flex flex-wrap items-center gap-4 mb-3">
          <span className="flex items-center gap-1.5 text-xs" style={{ color: COST_COLOR }}>
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: COST_COLOR }} /> Fixed Costs
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: REV_COLOR }}>
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: REV_COLOR }} /> Revenue
          </span>
          <span className="flex items-center gap-1.5 text-xs text-green-700">
            <span className="w-3 h-3 rounded-sm bg-green-700 inline-block" /> NOR +
          </span>
          <span className="flex items-center gap-1.5 text-xs text-red-600">
            <span className="w-3 h-3 rounded-sm bg-red-600 inline-block" /> NOR −
          </span>
          {trendSlope !== null && (
            <span className="flex items-center gap-1.5 text-xs" style={{ color: TREND_COLOR }}>
              {/* Dashed line icon */}
              <svg width="18" height="6" viewBox="0 0 18 6" style={{ flexShrink: 0 }}>
                <line x1="0"  y1="3" x2="5"  y2="3" stroke={TREND_COLOR} strokeWidth="2.5" />
                <line x1="7"  y1="3" x2="12" y2="3" stroke={TREND_COLOR} strokeWidth="2.5" />
                <line x1="14" y1="3" x2="18" y2="3" stroke={TREND_COLOR} strokeWidth="2.5" />
              </svg>
              NOR Trend (linear)
            </span>
          )}
        </div>

        {consolidatedData.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <ComposedChart data={trendData} barGap={3} barCategoryGap="22%">
              {commonGrid}
              {commonXAxis}
              <YAxis
                tickFormatter={yAxisFmt}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              {commonTooltip}

              {/* Fixed Costs bar */}
              <Bar dataKey="Fixed Costs" fill={COST_COLOR} radius={[3, 3, 0, 0]} maxBarSize={32}>
                {trendData.map((entry, idx) => (
                  <Cell
                    key={`c4a-${idx}`}
                    fill={entry._future ? '#e2e8f0' : COST_COLOR}
                    fillOpacity={entry._future ? 0.4 : 1}
                  />
                ))}
              </Bar>

              {/* Revenue bar */}
              <Bar dataKey="Revenue" fill={REV_COLOR} radius={[3, 3, 0, 0]} maxBarSize={32}>
                {trendData.map((entry, idx) => (
                  <Cell
                    key={`c4b-${idx}`}
                    fill={entry._future ? '#e2e8f0' : REV_COLOR}
                    fillOpacity={entry._future ? 0.4 : 1}
                  />
                ))}
              </Bar>

              {/* NOR bar — green if positive, red if negative */}
              <Bar dataKey="NOR" fill={NOR_POS_COLOR} radius={[3, 3, 0, 0]} maxBarSize={32}>
                {trendData.map((entry, idx) => (
                  <Cell
                    key={`c4c-${idx}`}
                    fill={entry._future ? '#e2e8f0' : norFill(entry['NOR'])}
                    fillOpacity={entry._future ? 0.4 : entry['NOR'] === 0 ? 0.35 : 1}
                  />
                ))}
              </Bar>

              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={1.5} />

              {/* NOR linear regression trend line */}
              {trendSlope !== null && (
                <Line
                  type="linear"
                  dataKey="NOR_trend"
                  name="NOR Trend"
                  stroke={TREND_COLOR}
                  strokeWidth={2.5}
                  strokeDasharray="7 4"
                  dot={false}
                  activeDot={{ r: 5, fill: TREND_COLOR, stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                  legendType="none"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <p className="text-[10px] text-slate-400 mt-2">
          All figures reconcile with "ALL VENUES" row in the yearly table above ·
          Trend uses OLS on {trendInfo ? `${trendInfo.n} months with data` : 'actual data'} only ·
          {trendInfo && trendSlope !== null && ` Monthly change: ${inr(trendSlope)}/month`}
        </p>
      </Card>

      {/* ── Chart 5 — Revenue per sq. ft. by Venue ─────────────────────────── */}
      {venuesWithArea.length > 0 && (
        <Card className="p-5 shadow-sm border-slate-200" data-testid="pnl-chart-sqft">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-800">Monthly Revenue / sq. ft. by Venue</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Total booking revenue ÷ revenue-generating area (sq. ft.) per month
                {venuesWithArea.length < venues.length && ` · ${venues.length - venuesWithArea.length} venue(s) excluded (no area data)`}
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={sqftData} margin={{ top: 8, right: 24, left: 10, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `₹${Number(v).toFixed(1)}`}
                width={56}
              />
              <Tooltip
                formatter={(v, name) => [v != null ? `₹${Number(v).toFixed(2)}/ft²` : '—', name]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,.06)' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {venuesWithArea.map((v, i) => (
                <Bar key={v} dataKey={v} name={v} fill={VENUE_PALETTE[i % VENUE_PALETTE.length]}
                  radius={[3, 3, 0, 0]} maxBarSize={48}>
                  {sqftData.map((entry, idx) => (
                    <Cell key={idx} fill={entry._future ? '#e2e8f0' : VENUE_PALETTE[i % VENUE_PALETTE.length]} />
                  ))}
                </Bar>
              ))}
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={1} />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-slate-400 mt-2">
            Only venues with "Revenue-Generating Area" set in Venue &amp; Court Master are included ·
            Set area in <strong>Venue &amp; Court Master → Edit Venue</strong> to add a venue
          </p>
        </Card>
      )}
    </div>
  );
}
